import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactElement } from "react";
import { Link } from "@tanstack/react-router";
import { ContentHeader, Field, QueryStatus, useToast } from "@cx/ui";
import type { CompanyFiscalProfile, NfeSettings } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ENVIRONMENT_LABEL } from "./labels";

const PROVIDER_LABEL: Record<NfeSettings["provider"], string> = {
  sandbox: "Provedor de testes (nenhuma nota vai à SEFAZ)",
  nuvemfiscal: "Nuvem Fiscal",
  disabled: "Desligado — a NF-e não pode ser enviada",
};

type SettingsForm = { environment: 1 | 2; series: string; nextNumber: string; autoEmail: boolean };

/**
 * Preferências › Fiscal › Nota fiscal eletrônica (T2.3): ambiente (homologação/produção), série, próximo número,
 * e-mail automático ao cliente e os dados do emitente que vão na NF-e (endereço com código IBGE, IM, CNAE).
 */
export function NfeSettingsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["nfe-settings", companyId];
  const q = useQuery({ queryKey: key, queryFn: () => api<NfeSettings>("/fiscal/nfe-settings") });
  const [f, setF] = useState<SettingsForm>({ environment: 2, series: "1", nextNumber: "1", autoEmail: true });
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data)
      setF({
        environment: q.data.environment,
        series: String(q.data.series),
        nextNumber: String(q.data.nextNumber),
        autoEmail: q.data.autoEmail,
      });
  }, [q.data]);
  const canEdit = can(perms, "invoices", "update");
  const canEnv = can(perms, "companies", "update");
  const save = useMutation({
    mutationFn: () =>
      api<NfeSettings>("/fiscal/nfe-settings", {
        method: "PUT",
        body: JSON.stringify({
          environment: f.environment,
          series: Number(f.series),
          nextNumber: Number(f.nextNumber),
          autoEmail: f.autoEmail,
        }),
      }),
    onSuccess: (s) => {
      setErrors({});
      qc.setQueryData(key, s);
      void qc.invalidateQueries({ queryKey: ["fiscal-certificate", companyId] });
      toast("success", "Configuração da NF-e salva");
    },
    onError: (e) => {
      setErrors(e instanceof ApiFailure && e.fields ? e.fields : {});
      toast("danger", errorMessage(e));
    },
  });
  const s = q.data;
  return (
    <>
      <ContentHeader
        title="Nota fiscal eletrônica"
        breadcrumb={["Preferências", "Fiscal", "Nota fiscal eletrônica"]}
        actions={
          canEdit && (
            <Link to="/preferencias/fiscal/nfe/inutilizacao" className="btn btn-outline-secondary">
              Inutilização de numeração
            </Link>
          )
        }
      />
      <section className="box card mb-3" aria-labelledby="nfe-settings">
        <div className="card-body">
          <h2 id="nfe-settings" className="h5">
            Emissão da NF-e
          </h2>
          <QueryStatus loading={q.isLoading} error={q.error} />
          {s && (
            <>
              <p className="text-muted small mb-2">
                Provedor fiscal: <strong>{PROVIDER_LABEL[s.provider]}</strong>.{" "}
                {s.lastUsedNumber > 0
                  ? `Último número usado na série ${s.series}: ${s.lastUsedNumber}.`
                  : "Nenhuma nota emitida nesta série ainda."}
              </p>
              {f.environment === 1 && s.environment === 2 && (
                <div className="alert alert-warning py-2" role="alert">
                  Em produção a nota tem valor fiscal. Confira o certificado digital e os dados do emitente
                  antes de salvar.
                </div>
              )}
              <form
                className="row align-items-end"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (canEdit) save.mutate();
                }}
              >
                <Field
                  label="Ambiente"
                  required
                  className="col-md-3"
                  error={errors.environment}
                  hint={canEnv ? undefined : "Mudar o ambiente exige permissão de alterar a empresa."}
                >
                  <select
                    className="form-select"
                    value={f.environment}
                    disabled={!canEdit || !canEnv}
                    onChange={(e) => setF({ ...f, environment: Number(e.target.value) as 1 | 2 })}
                  >
                    <option value={2}>{ENVIRONMENT_LABEL[2]} (testes)</option>
                    <option value={1}>{ENVIRONMENT_LABEL[1]}</option>
                  </select>
                </Field>
                <Field label="Série" required className="col-md-2" error={errors.series}>
                  <input
                    inputMode="numeric"
                    value={f.series}
                    disabled={!canEdit}
                    onChange={(e) => setF({ ...f, series: e.target.value.replace(/\D/g, "").slice(0, 3) })}
                  />
                </Field>
                <Field
                  label="Próximo número"
                  required
                  className="col-md-3"
                  error={errors.nextNumber}
                  hint="Sem pular números: a SEFAZ exige a sequência."
                >
                  <input
                    inputMode="numeric"
                    value={f.nextNumber}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setF({ ...f, nextNumber: e.target.value.replace(/\D/g, "").slice(0, 9) })
                    }
                  />
                </Field>
                <div className="col-md-4 mb-3">
                  <div className="form-check">
                    <input
                      id="nfe-auto-email"
                      type="checkbox"
                      className="form-check-input"
                      checked={f.autoEmail}
                      disabled={!canEdit}
                      onChange={(e) => setF({ ...f, autoEmail: e.target.checked })}
                    />
                    <label htmlFor="nfe-auto-email" className="form-check-label">
                      Enviar DANFE e XML ao cliente por e-mail ao autorizar
                    </label>
                  </div>
                </div>
                {canEdit && (
                  <div className="col-12 mb-1">
                    <button type="submit" className="btn btn-primary" disabled={save.isPending}>
                      Salvar configuração
                    </button>
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </section>
      <IssuerCard canEdit={canEnv} />
    </>
  );
}

type IssuerForm = {
  tradeName: string;
  im: string;
  cnae: string;
  phone: string;
  email: string;
  cep: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  cityIbge: string;
};
const EMPTY: IssuerForm = {
  tradeName: "",
  im: "",
  cnae: "",
  phone: "",
  email: "",
  cep: "",
  street: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  cityIbge: "",
};

/** Dados do emitente (`PUT /fiscal/company`): o que a NF-e e o cadastro no provedor fiscal exigem. */
function IssuerCard({ canEdit }: { canEdit: boolean }): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["fiscal-company", companyId];
  const q = useQuery({ queryKey: key, queryFn: () => api<CompanyFiscalProfile>("/fiscal/company") });
  const [f, setF] = useState<IssuerForm>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data) {
      const a = q.data.address;
      setF({
        tradeName: q.data.tradeName ?? "",
        im: q.data.im ?? "",
        cnae: q.data.cnae ?? "",
        phone: q.data.phone ?? "",
        email: q.data.email ?? "",
        cep: a.cep ?? "",
        street: a.street ?? "",
        number: a.number ?? "",
        complement: a.complement ?? "",
        district: a.district ?? "",
        city: a.city ?? "",
        cityIbge: a.cityIbge ?? "",
      });
    }
  }, [q.data]);
  const save = useMutation({
    mutationFn: () =>
      api<CompanyFiscalProfile>("/fiscal/company", {
        method: "PUT",
        body: JSON.stringify({
          tradeName: f.tradeName,
          im: f.im,
          cnae: f.cnae,
          phone: f.phone,
          email: f.email,
          address: {
            cep: f.cep,
            street: f.street,
            number: f.number,
            complement: f.complement,
            district: f.district,
            city: f.city,
            cityIbge: f.cityIbge,
          },
        }),
      }),
    onSuccess: (p) => {
      setErrors({});
      qc.setQueryData(key, p);
      toast("success", "Dados do emitente salvos");
    },
    onError: (e) => {
      // Erros do endereço chegam como "address.cep": o campo mostra só a última parte.
      const fields = e instanceof ApiFailure && e.fields ? e.fields : {};
      setErrors(Object.fromEntries(Object.entries(fields).map(([k, v]) => [k.replace(/^address\./, ""), v])));
      toast("danger", errorMessage(e));
    },
  });
  const input = (
    k: keyof IssuerForm,
    label: string,
    cls: string,
    o: { max?: number; required?: boolean; hint?: string } = {},
  ) => (
    <Field label={label} required={o.required} className={cls} error={errors[k]} hint={o.hint}>
      <input
        value={f[k]}
        maxLength={o.max ?? 60}
        disabled={!canEdit}
        onChange={(e) => setF({ ...f, [k]: e.target.value })}
      />
    </Field>
  );
  return (
    <section className="box card mb-3" aria-labelledby="nfe-issuer">
      <div className="card-body">
        <h2 id="nfe-issuer" className="h5">
          Dados do emitente
        </h2>
        <QueryStatus loading={q.isLoading} error={q.error} />
        {q.data && (
          <>
            <p className="text-muted small">
              {q.data.legalName}
              {q.data.uf && ` — ${q.data.uf}`}. CNPJ, IE, UF e CRT ficam em Naturezas de operação; aqui, o que
              a nota também exige.
            </p>
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                if (canEdit) save.mutate();
              }}
            >
              {input("tradeName", "Nome fantasia", "col-md-6")}
              {input("im", "Inscrição municipal", "col-md-3", { max: 15 })}
              {input("cnae", "CNAE", "col-md-3", { max: 7, hint: "7 dígitos (obrigatório com IM)." })}
              {input("phone", "Telefone", "col-md-4", { max: 20 })}
              {input("email", "E-mail", "col-md-8", { max: 254 })}
              {input("cep", "CEP", "col-md-3", { max: 9, required: true })}
              {input("street", "Logradouro", "col-md-6", { required: true })}
              {input("number", "Número", "col-md-3", { required: true })}
              {input("complement", "Complemento", "col-md-4")}
              {input("district", "Bairro", "col-md-4", { required: true })}
              {input("city", "Município", "col-md-4", { required: true })}
              {input("cityIbge", "Código IBGE do município", "col-md-4", {
                max: 7,
                required: true,
                hint: "7 dígitos (ex.: 3550308 = São Paulo).",
              })}
              {canEdit && (
                <div className="col-12">
                  <button type="submit" className="btn btn-outline-primary" disabled={save.isPending}>
                    Salvar dados do emitente
                  </button>
                </div>
              )}
            </form>
          </>
        )}
      </div>
    </section>
  );
}
